import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Save, LogOut, ExternalLink, Plus, Trash2, ChevronDown, ChevronUp,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";

import ImageUploader from "@/components/admin/ImageUploader";
import { TextInput, TextArea, StringList, Card } from "@/components/admin/Form";

const TABS = [
  { id: "site", label: "Tinklalapis" },
  { id: "nav", label: "Meniu" },
  { id: "home", label: "Pagrindinis" },
  { id: "about", label: "Apie mane" },
  { id: "projects", label: "Projektai" },
  { id: "standards", label: "Standartas" },
  { id: "pricing", label: "Kainos" },
  { id: "seo", label: "SEO" },
  { id: "advanced", label: "Papildomai" },
  { id: "contacts", label: "Užklausos" },
];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const { content, refresh, setContent } = useContent();
  const [draft, setDraft] = useState(content);
  const [tab, setTab] = useState("site");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(content);
  }, [content]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(content),
    [draft, content]
  );

  const update = (path, value) => {
    setDraft((prev) => {
      const next = structuredClone(prev || {});
      const parts = path.split(".");
      let obj = next;
      for (let i = 0; i < parts.length - 1; i += 1) {
        const k = parts[i];
        if (obj[k] == null || typeof obj[k] !== "object") obj[k] = {};
        obj = obj[k];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", draft);
      setContent(draft);
      await refresh();
      toast.success("Pakeitimai išsaugoti");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-[#e5e2dd] bg-[#f9f8f6] sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="font-serif-display italic text-[#a66e4b] text-xl">
              MANOPIRTIS.LT
            </div>
            <span className="overline text-[#7a7570] hidden sm:inline">
              Administravimas · {admin?.username}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="text-[11px] uppercase tracking-[0.22em] text-[#5c5c5c] hover:text-[#1a1a1a] inline-flex items-center gap-1"
            >
              Peržiūrėti <ExternalLink size={12} />
            </a>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              data-testid="admin-save"
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.22em] bg-[#a66e4b] text-[#0f0f0f] hover:bg-[#bd825c] disabled:opacity-40 transition"
            >
              <Save size={14} /> {saving ? "Saugoma…" : dirty ? "Išsaugoti" : "Išsaugota"}
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="text-[11px] uppercase tracking-[0.22em] text-[#5c5c5c] hover:text-[#e57373] inline-flex items-center gap-1"
            >
              <LogOut size={12} /> Atsijungti
            </button>
          </div>
        </div>
        <nav className="max-w-[1400px] mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`whitespace-nowrap px-4 py-3 text-[11px] uppercase tracking-[0.22em] border-b-2 ${
                tab === t.id
                  ? "border-[#a66e4b] text-[#1a1a1a]"
                  : "border-transparent text-[#7a7570] hover:text-[#1a1a1a]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="flex-1 max-w-[1400px] mx-auto w-full px-6 py-10">
        {tab === "site" && <SiteTab draft={draft} update={update} />}
        {tab === "nav" && <NavTab draft={draft} update={update} />}
        {tab === "home" && <HomeTab draft={draft} update={update} />}
        {tab === "about" && <AboutTab draft={draft} update={update} />}
        {tab === "projects" && <ProjectsTab draft={draft} update={update} />}
        {tab === "standards" && <StandardsTab draft={draft} update={update} />}
        {tab === "pricing" && <PricingTab draft={draft} update={update} />}
        {tab === "seo" && <SeoTab draft={draft} update={update} />}
        {tab === "advanced" && <AdvancedTab draft={draft} setDraft={setDraft} />}
        {tab === "contacts" && <ContactsTab />}
      </main>
    </div>
  );
}

/* ----------------- Tabs ----------------- */

function SiteTab({ draft, update }) {
  const site = draft.site || {};
  const assets = draft.assets || {};
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card title="Brand">
        <div className="space-y-4">
          <TextInput label="Pavadinimas (wordmark)" value={site.brand} onChange={(v) => update("site.brand", v)} />
          <TextInput label="Brand tagline" value={site.brandTagline} onChange={(v) => update("site.brandTagline", v)} />
          <TextInput label="Trumpas filosofijos sakinys" value={site.philosophyShort} onChange={(v) => update("site.philosophyShort", v)} />
          <TextInput label="Autoriaus citata (hero apačioje)" value={site.authorQuote} onChange={(v) => update("site.authorQuote", v)} />
          <TextArea label="Pastaba apie sezoną" rows={3} value={site.seasonNote} onChange={(v) => update("site.seasonNote", v)} />
        </div>
      </Card>
      <Card title="Kontaktai">
        <div className="space-y-4">
          <TextInput label="Miestas" value={site.city} onChange={(v) => update("site.city", v)} />
          <TextInput label="Šalis" value={site.country} onChange={(v) => update("site.country", v)} />
          <TextInput label="Telefonas (rodomas)" value={site.phone} onChange={(v) => update("site.phone", v)} />
          <TextInput label="Telefonas (tel: nuoroda)" value={site.phoneHref} onChange={(v) => update("site.phoneHref", v)} />
          <TextInput label="El. paštas" value={site.email} onChange={(v) => update("site.email", v)} />
          <TextInput label="Instagram URL" value={site.instagram} onChange={(v) => update("site.instagram", v)} />
          <TextInput label="Facebook URL" value={site.facebook} onChange={(v) => update("site.facebook", v)} />
        </div>
      </Card>
      <Card title="Pagrindiniai paveikslėliai">
        <div className="space-y-6">
          <ImageUploader testid="upload-hero" label="Hero (pradžios) paveikslėlis" value={assets.hero} onChange={(v) => update("assets.hero", v)} />
          <ImageUploader testid="upload-portrait" label="Autoriaus portretas" value={assets.authorPortrait} onChange={(v) => update("assets.authorPortrait", v)} />
          <ImageUploader testid="upload-finalbg" label="Atsarginis fonas (jei naudosi)" value={assets.finalBg} onChange={(v) => update("assets.finalBg", v)} />
        </div>
      </Card>
    </div>
  );
}

function NavTab({ draft, update }) {
  const nav = draft.nav || [];
  const set = (next) => update("nav", next);
  const setItem = (i, key, v) => {
    const copy = [...nav];
    copy[i] = { ...copy[i], [key]: v };
    set(copy);
  };
  const add = () => set([...nav, { to: "/naujas", label: "Naujas" }]);
  const remove = (i) => set(nav.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= nav.length) return;
    const copy = [...nav];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    set(copy);
  };
  return (
    <Card title="Meniu punktai" actions={
      <button type="button" onClick={add} className="text-[11px] uppercase tracking-[0.22em] text-[#a66e4b] hover:text-[#d09a72] inline-flex items-center gap-1">
        <Plus size={12} /> Pridėti
      </button>
    }>
      <p className="text-xs text-[#7a7570] mb-6 font-light">
        {`Svarbu: vidiniai keliai negali prasidėti „/api" – Kubernetes ingress nukreips į backend. „/kainos" desktop'e paslėpta, bet matoma mobiliame menu ir footeryje.`}
      </p>
      <ul className="space-y-4">
        {nav.map((item, i) => (
          <li key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-center border border-[#e5e2dd] p-3">
            <TextInput label="Antraštė" value={item.label} onChange={(v) => setItem(i, "label", v)} />
            <TextInput label="Kelias (pvz. /apie)" value={item.to} onChange={(v) => setItem(i, "to", v)} />
            <div className="flex gap-1 justify-end">
              <button type="button" onClick={() => move(i, -1)} className="p-2 text-[#7a7570] hover:text-[#1a1a1a]"><ChevronUp size={14} /></button>
              <button type="button" onClick={() => move(i, +1)} className="p-2 text-[#7a7570] hover:text-[#1a1a1a]"><ChevronDown size={14} /></button>
              <button type="button" onClick={() => remove(i)} className="p-2 text-[#e57373] hover:text-[#ff8a8a]"><Trash2 size={14} /></button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function HomeTab({ draft, update }) {
  const hero = draft.hero || {};
  const hp = draft.homePhilosophy || {};
  const teaser = draft.homeProjectsTeaser || {};
  const stats = hp.stats || [];
  return (
    <div className="space-y-6">
      <Card title="Pradžios puslapis · Hero">
        <div className="space-y-5">
          <TextInput label="Smulkus užrašas viršuje (palaikoma {city})" value={hero.labelLine} onChange={(v) => update("hero.labelLine", v)} />
          <TextInput label="Antraštė – pirma eilutė" value={hero.headlineTop} onChange={(v) => update("hero.headlineTop", v)} />
          <TextInput label="Antraštė – antra eilutė (italic)" value={hero.headlineBottom} onChange={(v) => update("hero.headlineBottom", v)} />
          <div className="grid grid-cols-2 gap-4">
            <TextInput label="Pirmas mygtukas – tekstas" value={hero.ctaPrimary?.label} onChange={(v) => update("hero.ctaPrimary.label", v)} />
            <TextInput label="Pirmas mygtukas – nuoroda" value={hero.ctaPrimary?.to} onChange={(v) => update("hero.ctaPrimary.to", v)} />
            <TextInput label="Antras mygtukas – tekstas" value={hero.ctaSecondary?.label} onChange={(v) => update("hero.ctaSecondary.label", v)} />
            <TextInput label="Antras mygtukas – nuoroda" value={hero.ctaSecondary?.to} onChange={(v) => update("hero.ctaSecondary.to", v)} />
          </div>
        </div>
      </Card>

      <Card title="Pagrindinis · Filosofijos blokas">
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <TextInput label="Antraštė – pirma eilutė" value={hp.headlineTop} onChange={(v) => update("homePhilosophy.headlineTop", v)} />
            <TextInput label="Antraštė – antra eilutė (italic)" value={hp.headlineBottom} onChange={(v) => update("homePhilosophy.headlineBottom", v)} />
          </div>
          <StringList label="Pastraipos" items={hp.paragraphs || []} onChange={(v) => update("homePhilosophy.paragraphs", v)} />
          <div>
            <div className="overline text-[#5c5c5c] mb-3">Statistikos (3 vnt.)</div>
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-3 border border-[#e5e2dd] p-3">
                  <TextInput label="Skaičius" value={stats[i]?.n} onChange={(v) => {
                    const copy = [...stats]; copy[i] = { ...copy[i], n: v }; update("homePhilosophy.stats", copy);
                  }} />
                  <TextInput label="Pavadinimas" value={stats[i]?.label} onChange={(v) => {
                    const copy = [...stats]; copy[i] = { ...copy[i], label: v }; update("homePhilosophy.stats", copy);
                  }} />
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TextInput label="CTA – tekstas" value={hp.ctaLabel} onChange={(v) => update("homePhilosophy.ctaLabel", v)} />
            <TextInput label="CTA – nuoroda" value={hp.ctaTo} onChange={(v) => update("homePhilosophy.ctaTo", v)} />
          </div>
        </div>
      </Card>

      <Card title="Pagrindinis · Projektų peržiūra">
        <div className="space-y-4">
          <TextInput label="Antraštė – pirma eilutė" value={teaser.headlineTop} onChange={(v) => update("homeProjectsTeaser.headlineTop", v)} />
          <TextInput label="Antraštė – antra eilutė (italic)" value={teaser.headlineBottom} onChange={(v) => update("homeProjectsTeaser.headlineBottom", v)} />
          <TextArea label="Įžanga (dešinėje pusėje)" rows={3} value={teaser.intro} onChange={(v) => update("homeProjectsTeaser.intro", v)} />
        </div>
      </Card>
    </div>
  );
}

function AboutTab({ draft, update }) {
  const about = draft.about || {};
  const stats = about.stats || [];
  return (
    <div className="space-y-6">
      <Card title="Antraštė ir tekstas">
        <div className="space-y-5">
          <TextInput label="Antraštė" value={about.headline} onChange={(v) => update("about.headline", v)} />
          <StringList label="Pastraipos" items={about.paragraphs || []} onChange={(v) => update("about.paragraphs", v)} testid="about-paragraphs" />
          <TextArea label="Citata (dešinėje pusėje)" rows={4} value={about.blockquote} onChange={(v) => update("about.blockquote", v)} />
        </div>
      </Card>
      <Card title="Statistikos blokai (3 vnt.)">
        <div className="grid grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-3 border border-[#e5e2dd] p-3">
              <TextInput label="Skaičius" value={stats[i]?.n} onChange={(v) => {
                const copy = [...stats]; copy[i] = { ...copy[i], n: v }; update("about.stats", copy);
              }} />
              <TextInput label="Pavadinimas" value={stats[i]?.label} onChange={(v) => {
                const copy = [...stats]; copy[i] = { ...copy[i], label: v }; update("about.stats", copy);
              }} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function ProjectsTab({ draft, update }) {
  const projects = draft.projects || [];
  const page = draft.projectsPage || {};
  const setItem = (i, key, v) => {
    const copy = [...projects]; copy[i] = { ...copy[i], [key]: v }; update("projects", copy);
  };
  const add = () => update("projects", [...projects, {
    id: `naujas-${Date.now()}`, slug: "naujas", label: "0X — NAUJAS", name: "Naujas projektas",
    area: "", dims: "", tagline: "", priceFrom: "", priceShort: "", priceNote: "",
    cover: "", gallery: [], intro: "", bullets: [], idea: "", sections: [], important: "",
  }]);
  const remove = (i) => update("projects", projects.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= projects.length) return;
    const copy = [...projects]; [copy[i], copy[j]] = [copy[j], copy[i]]; update("projects", copy);
  };
  return (
    <div className="space-y-6">
      <Card title="Projektų puslapio antraštė">
        <div className="space-y-4">
          <TextInput label="Antraštė – pirma eilutė" value={page.headlineTop} onChange={(v) => update("projectsPage.headlineTop", v)} />
          <TextInput label="Antraštė – antra eilutė (italic)" value={page.headlineBottom} onChange={(v) => update("projectsPage.headlineBottom", v)} />
          <TextArea label="Įžanga (dešinėje pusėje)" rows={3} value={page.intro} onChange={(v) => update("projectsPage.intro", v)} />
        </div>
      </Card>

      <div className="flex justify-end">
        <button type="button" onClick={add} className="text-[11px] uppercase tracking-[0.22em] text-[#a66e4b] hover:text-[#d09a72] inline-flex items-center gap-1">
          <Plus size={12} /> Pridėti projektą
        </button>
      </div>
      {projects.map((p, i) => (
        <Card key={p.id} title={p.name || "Projektas"} actions={
          <div className="flex gap-2">
            <button type="button" onClick={() => move(i, -1)} className="p-1 text-[#7a7570] hover:text-[#1a1a1a]"><ChevronUp size={14} /></button>
            <button type="button" onClick={() => move(i, +1)} className="p-1 text-[#7a7570] hover:text-[#1a1a1a]"><ChevronDown size={14} /></button>
            <button type="button" onClick={() => remove(i)} className="p-1 text-[#e57373] hover:text-[#ff8a8a]"><Trash2 size={14} /></button>
          </div>
        }>
          <div className="grid md:grid-cols-2 gap-5">
            <TextInput label="ID (unikalus, lotyniškas)" value={p.id} onChange={(v) => setItem(i, "id", v)} />
            <TextInput label="Slug (URL fragmentas)" value={p.slug} onChange={(v) => setItem(i, "slug", v)} />
            <TextInput label="Etiketė (pvz. 01 — SOLO)" value={p.label} onChange={(v) => setItem(i, "label", v)} />
            <TextInput label="Pavadinimas" value={p.name} onChange={(v) => setItem(i, "name", v)} />
            <TextInput label="Plotas" value={p.area} onChange={(v) => setItem(i, "area", v)} />
            <TextInput label="Matmenys" value={p.dims} onChange={(v) => setItem(i, "dims", v)} />
            <TextInput label="Tagline (po pavadinimu)" value={p.tagline} onChange={(v) => setItem(i, "tagline", v)} />
            <TextInput label="Kaina nuo" value={p.priceFrom} onChange={(v) => setItem(i, "priceFrom", v)} />
            <TextInput label="Kainos kortelės pavadinimas" value={p.priceShort} onChange={(v) => setItem(i, "priceShort", v)} />
          </div>
          <div className="mt-5 space-y-5">
            <TextArea label="Įžanga (vienas paragrafas)" rows={3} value={p.intro} onChange={(v) => setItem(i, "intro", v)} />
            <TextArea label="Kainos kortelės aprašymas" rows={2} value={p.priceNote} onChange={(v) => setItem(i, "priceNote", v)} />
            <TextArea label="Idėja" rows={2} value={p.idea} onChange={(v) => setItem(i, "idea", v)} />
            <StringList label="Trumpos savybės (rodomos projektų sąraše)" items={p.bullets || []} onChange={(v) => setItem(i, "bullets", v)} />
          </div>
          <div className="mt-6">
            <SectionsEditor value={p.sections || []} onChange={(v) => setItem(i, "sections", v)} />
          </div>
          <div className="mt-6">
            <TextArea label={`„Svarbu" sekcija (parodoma modalo apačioje)`} rows={4} value={p.important} onChange={(v) => setItem(i, "important", v)} />
          </div>
          <div className="mt-6 space-y-5">
            <ImageUploader label="Pagrindinė nuotrauka" value={p.cover} onChange={(v) => setItem(i, "cover", v)} />
            <ImageUploader label="Planas (patalpų brėžinys)" value={p.plan} onChange={(v) => setItem(i, "plan", v)} />
            <GalleryEditor value={p.gallery || []} onChange={(v) => setItem(i, "gallery", v)} />
          </div>
        </Card>
      ))}
    </div>
  );
}

function SectionsEditor({ value, onChange }) {
  const update = (idx, key, v) => {
    const copy = [...value];
    copy[idx] = { ...copy[idx], [key]: v };
    onChange(copy);
  };
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= value.length) return;
    const copy = [...value]; [copy[i], copy[j]] = [copy[j], copy[i]]; onChange(copy);
  };
  return (
    <div className="space-y-4">
      <div className="overline text-[#5c5c5c]">Detalūs skyriai (modale)</div>
      <p className="text-[11px] text-[#7a7570] font-light">
        Eilutės, pradedančios „— " (brūkšnys), bus rodomos kaip subpunktai (su atitraukimu).
      </p>
      {value.map((sec, i) => (
        <div key={i} className="border border-[#e5e2dd] p-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-xs text-[#7a7570] uppercase tracking-[0.2em]">Skyrius {i + 1}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} className="p-1 text-[#7a7570] hover:text-[#1a1a1a]"><ChevronUp size={12} /></button>
              <button type="button" onClick={() => move(i, +1)} className="p-1 text-[#7a7570] hover:text-[#1a1a1a]"><ChevronDown size={12} /></button>
              <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="p-1 text-[#e57373] hover:text-[#ff8a8a]"><Trash2 size={12} /></button>
            </div>
          </div>
          <TextInput label="Skyriaus pavadinimas" value={sec.title} onChange={(v) => update(i, "title", v)} />
          <StringList label="Punktai" items={sec.items || []} onChange={(v) => update(i, "items", v)} />
          <TextArea label="Pastaba po sąrašo (nebūtina)" rows={2} value={sec.note} onChange={(v) => update(i, "note", v)} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, { title: "Naujas skyrius", items: [""], note: "" }])} className="text-[11px] uppercase tracking-[0.22em] text-[#a66e4b] hover:text-[#d09a72]">
        + Pridėti skyrių
      </button>
    </div>
  );
}

function GalleryEditor({ value, onChange }) {
  return (
    <div className="space-y-3">
      <div className="overline text-[#5c5c5c]">Galerija</div>
      {value.map((url, i) => (
        <div key={i} className="border border-[#e5e2dd] p-3">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs text-[#7a7570]">Nuotrauka {i + 1}</span>
            <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="text-[11px] text-[#e57373] hover:text-[#ff8a8a]">Pašalinti</button>
          </div>
          <ImageUploader value={url} onChange={(v) => {
            const copy = [...value]; copy[i] = v; onChange(copy);
          }} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, ""])} className="text-[11px] uppercase tracking-[0.22em] text-[#a66e4b] hover:text-[#d09a72]">+ Pridėti nuotrauką</button>
    </div>
  );
}

function StandardsTab({ draft, update }) {
  const standards = draft.standards || [];
  const setItem = (i, key, v) => {
    const copy = [...standards]; copy[i] = { ...copy[i], [key]: v }; update("standards", copy);
  };
  const add = () => update("standards", [...standards, {
    id: `naujas-${Date.now()}`, icon: "Compass", title: "Naujas standartas", body: [""], bullets: [], footer: "",
  }]);
  const remove = (i) => update("standards", standards.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= standards.length) return;
    const copy = [...standards]; [copy[i], copy[j]] = [copy[j], copy[i]]; update("standards", copy);
  };
  return (
    <div className="space-y-6">
      <Card title="Uždarymo citata">
        <TextArea rows={3} value={draft.standardQuote} onChange={(v) => update("standardQuote", v)} />
      </Card>
      <div className="flex justify-end">
        <button type="button" onClick={add} className="text-[11px] uppercase tracking-[0.22em] text-[#a66e4b] hover:text-[#d09a72] inline-flex items-center gap-1">
          <Plus size={12} /> Pridėti standartą
        </button>
      </div>
      {standards.map((s, i) => (
        <Card key={s.id} title={s.title || "Standartas"} actions={
          <div className="flex gap-2">
            <button type="button" onClick={() => move(i, -1)} className="p-1 text-[#7a7570] hover:text-[#1a1a1a]"><ChevronUp size={14} /></button>
            <button type="button" onClick={() => move(i, +1)} className="p-1 text-[#7a7570] hover:text-[#1a1a1a]"><ChevronDown size={14} /></button>
            <button type="button" onClick={() => remove(i)} className="p-1 text-[#e57373] hover:text-[#ff8a8a]"><Trash2 size={14} /></button>
          </div>
        }>
          <div className="grid md:grid-cols-3 gap-4 mb-5">
            <TextInput label="ID" value={s.id} onChange={(v) => setItem(i, "id", v)} />
            <TextInput label="Pavadinimas" value={s.title} onChange={(v) => setItem(i, "title", v)} />
            <TextInput label="Icon (Grid3x3, Layers, Flame, Compass, Lightbulb, SlidersHorizontal)" value={s.icon} onChange={(v) => setItem(i, "icon", v)} />
          </div>
          <StringList label="Pastraipos" items={s.body || []} onChange={(v) => setItem(i, "body", v)} />
          <div className="mt-5">
            <StringList label="Sąrašo punktai (jei reikia)" items={s.bullets || []} onChange={(v) => setItem(i, "bullets", v)} />
          </div>
          <div className="mt-5">
            <TextArea label="Pabaigos pastraipa" rows={2} value={s.footer} onChange={(v) => setItem(i, "footer", v)} />
          </div>
        </Card>
      ))}
    </div>
  );
}

function PricingTab({ draft, update }) {
  const intro = draft.pricingIntro || {};
  return (
    <div className="space-y-6">
      <Card title="Įžanga">
        <div className="space-y-5">
          <TextInput label="Antraštė" value={intro.headline} onChange={(v) => update("pricingIntro.headline", v)} />
          <StringList label="Tekstas (paragrafai)" items={intro.body || []} onChange={(v) => update("pricingIntro.body", v)} />
        </div>
      </Card>
      <Card title="Kainai įtaką darantys veiksniai">
        <StringList items={draft.pricingFactors || []} onChange={(v) => update("pricingFactors", v)} testid="pricing-factors-editor" />
      </Card>
      <Card title="Modelių kainų kortelės">
        <p className="text-xs text-[#7a7570] font-light">
          {`Modelių sąrašas redaguojamas tabe „Projektai". Čia rodomos tos pačios reikšmės iš ten („Kaina nuo" + „Kainos kortelės pavadinimas" + „Kainos kortelės aprašymas").`}
        </p>
      </Card>
    </div>
  );
}

function SeoTab({ draft, update }) {
  const seo = draft.seo || {};
  const global = draft.seoGlobal || {};
  const nav = draft.nav || [];
  const routes = ["/", ...nav.map((n) => n.to).filter((r) => r && r !== "/")];
  const unique = Array.from(new Set(routes));
  return (
    <div className="space-y-6">
      <Card title="SEO — globalūs nustatymai">
        <div className="grid md:grid-cols-2 gap-4">
          <TextInput label="Svetainės pavadinimas (siteName)" value={global.siteName} onChange={(v) => update("seoGlobal.siteName", v)} />
          <TextInput label="Kanoninis URL (https://...)" value={global.siteUrl} onChange={(v) => update("seoGlobal.siteUrl", v)} />
          <TextInput label="Numatytasis Title" value={global.defaultTitle} onChange={(v) => update("seoGlobal.defaultTitle", v)} />
          <TextInput label="Robots (index,follow / noindex,nofollow)" value={global.robots} onChange={(v) => update("seoGlobal.robots", v)} />
          <TextInput label="Locale (pvz. lt_LT)" value={global.locale} onChange={(v) => update("seoGlobal.locale", v)} />
          <TextInput label="Theme color (#hex)" value={global.themeColor} onChange={(v) => update("seoGlobal.themeColor", v)} />
          <TextInput label="Geo region (LT-KL)" value={global.geoRegion} onChange={(v) => update("seoGlobal.geoRegion", v)} />
          <TextInput label="Geo placename (Klaipėda)" value={global.geoPlacename} onChange={(v) => update("seoGlobal.geoPlacename", v)} />
          <TextInput label="Twitter @handle" value={global.twitterHandle} onChange={(v) => update("seoGlobal.twitterHandle", v)} />
          <TextInput label="Google Search Console verification code" value={global.googleVerification} onChange={(v) => update("seoGlobal.googleVerification", v)} />
        </div>
        <div className="mt-5 space-y-4">
          <TextArea label="Numatytasis Description" rows={3} value={global.defaultDescription} onChange={(v) => update("seoGlobal.defaultDescription", v)} />
          <TextArea label="Keywords (kableliais atskirti)" rows={2} value={global.keywords} onChange={(v) => update("seoGlobal.keywords", v)} />
          <ImageUploader label="Numatytasis OG paveikslėlis (kai puslapis neturi savo)" value={global.defaultOgImage} onChange={(v) => update("seoGlobal.defaultOgImage", v)} />
        </div>
        <p className="text-[11px] text-[#7a7570] mt-5 font-light">
          {`Automatiškai generuojami: sitemap.xml (/api/sitemap.xml), robots.txt (/api/robots.txt), JSON-LD LocalBusiness + Products + BreadcrumbList schema, OG + Twitter Card meta.`}
        </p>
      </Card>

      <p className="text-xs text-[#7a7570] font-light">
        {`Per-puslapinis SEO (title / description / OG paveikslėlis). Jei paliksite tuščią — bus naudojami globalūs numatytieji.`}
      </p>
      {unique.map((path) => {
        const d = seo[path] || {};
        const setSeo = (k, v) => {
          const copy = { ...seo, [path]: { ...d, [k]: v } };
          update("seo", copy);
        };
        return (
          <Card key={path} title={path}>
            <div className="space-y-4">
              <TextInput label="Title (naršyklės skirtukas)" value={d.title} onChange={(v) => setSeo("title", v)} />
              <TextArea label="Meta description" rows={3} value={d.description} onChange={(v) => setSeo("description", v)} />
              <ImageUploader label="OpenGraph paveikslėlis (socialiniams tinklams)" value={d.ogImage} onChange={(v) => setSeo("ogImage", v)} />
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function AdvancedTab({ draft, setDraft }) {
  const [text, setText] = useState(() => JSON.stringify(draft, null, 2));
  const [err, setErr] = useState("");
  useEffect(() => { setText(JSON.stringify(draft, null, 2)); }, [draft]);
  const apply = () => {
    try {
      const parsed = JSON.parse(text);
      setDraft(parsed);
      setErr("");
      toast.success('JSON pritaikytas – spauskite „Išsaugoti" viršuje');
    } catch (e) {
      setErr(e.message);
    }
  };
  return (
    <div className="space-y-4">
      <Card title="Papildomi nustatymai — visos svetainės JSON">
        <p className="text-xs text-[#7a7570] font-light mb-4">
          {`Čia galite tiesiogiai redaguoti visą svetainės turinį — įskaitant „labels" (visas UI etiketes), meniu, projektus, standartus, SEO. Naudokite atsargiai: neteisingas JSON sugadins svetainę. Nespauskite „Pritaikyti" kol neįsitikinsite, kad JSON teisingas.`}
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={28}
          className="w-full bg-[#f9f8f6] border border-[#e5e2dd] focus:border-[#a66e4b] outline-none p-3 text-[#1a1a1a] font-mono text-xs resize-y"
        />
        {err && (
          <div className="mt-3 text-sm text-[#e57373]">JSON klaida: {err}</div>
        )}
        <div className="mt-4 flex gap-3">
          <button type="button" onClick={apply} className="px-4 py-2 bg-[#a66e4b] text-[#0f0f0f] text-[11px] uppercase tracking-[0.22em] hover:bg-[#bd825c]">
            Pritaikyti JSON
          </button>
          <button type="button" onClick={() => setText(JSON.stringify(draft, null, 2))} className="px-4 py-2 border border-[#d8d4ce] text-[#5c5c5c] text-[11px] uppercase tracking-[0.22em] hover:text-[#1a1a1a]">
            Atkurti
          </button>
        </div>
      </Card>
    </div>
  );
}

function ContactsTab() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get("/admin/contacts").then(({ data }) => setItems(data)).catch((e) => setError(e));
  }, []);
  if (error) return <Card title="Užklausos"><div className="text-sm text-[#e57373]">Nepavyko gauti užklausų.</div></Card>;
  if (!items) return <Card title="Užklausos"><div className="text-sm text-[#7a7570]">Kraunama…</div></Card>;
  if (items.length === 0) return <Card title="Užklausos"><div className="text-sm text-[#7a7570]">Užklausų dar nėra.</div></Card>;
  return (
    <Card title={`Užklausos (${items.length})`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.22em] text-[#7a7570]">
              <th className="text-left p-2">Data</th>
              <th className="text-left p-2">Vardas</th>
              <th className="text-left p-2">El. paštas</th>
              <th className="text-left p-2">Tel.</th>
              <th className="text-left p-2">Modelis</th>
              <th className="text-left p-2">Žinutė</th>
            </tr>
          </thead>
          <tbody className="text-[#3a3a3a]">
            {items.map((c) => (
              <tr key={c.id} className="border-t border-[#e5e2dd] align-top">
                <td className="p-2 whitespace-nowrap font-light text-xs">
                  {(c.created_at || "").replace("T", " ").slice(0, 16)}
                </td>
                <td className="p-2 font-light">{c.name}</td>
                <td className="p-2 font-light"><a href={`mailto:${c.email}`} className="hover:text-[#a66e4b]">{c.email}</a></td>
                <td className="p-2 font-light">{c.phone || "—"}</td>
                <td className="p-2 font-light text-[#a66e4b]">{c.project_interest || "—"}</td>
                <td className="p-2 font-light text-xs max-w-md whitespace-pre-wrap">{c.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
